import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, ArrowUpRight, LogOut, Building2, Truck, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import NoderaMark from "@/components/NoderaMark";

const NAV = [
  { to: "/platform", label: "Platform" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/programs", label: "Programs" },
  { to: "/vendors", label: "Vendors" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Blog" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const portalRef = useRef(null);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => setPortalOpen(false), [location.pathname]);

  // Close the Portal dropdown when clicking outside it. We bind on the
  // next tick so the click that OPENED the dropdown doesn't immediately
  // close it via the same event bubbling up to the document.
  useEffect(() => {
    if (!portalOpen) return;
    const onClick = (e) => {
      if (portalRef.current && !portalRef.current.contains(e.target)) setPortalOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setPortalOpen(false); };
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onClick);
    }, 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [portalOpen]);

  const PortalDropdown = ({ mobile = false }) => (
    <div className={`relative ${mobile ? "w-full" : ""}`} ref={portalRef}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setPortalOpen((v) => !v); }}
        data-testid={mobile ? "mobile-nav-portal-dropdown-btn" : "nav-portal-dropdown-btn"}
        aria-haspopup="menu"
        aria-expanded={portalOpen}
        className={`${mobile ? "w-full justify-between" : ""} px-3 py-1.5 rounded-full text-xs font-semibold text-slate-900 bg-slate-100/70 hover:bg-slate-200 border border-slate-200 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap`}
      >
        Portal
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${portalOpen ? "rotate-180" : ""}`} />
      </button>
      {portalOpen && (
        <div
          role="menu"
          data-testid={mobile ? "mobile-nav-portal-dropdown-menu" : "nav-portal-dropdown-menu"}
          className={`${mobile
            ? "mt-1 w-full"
            : "absolute right-0 mt-2 w-64"
          } rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden z-50`}
        >
          <Link
            to="/login"
            data-testid={mobile ? "mobile-nav-clinic-login-btn" : "nav-clinic-login-btn"}
            onClick={() => setPortalOpen(false)}
            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100"
          >
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
              <Building2 className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">Clinic Login</span>
              <span className="block text-[11px] text-slate-500 leading-snug">For clinics deploying cash-pay programs</span>
            </span>
          </Link>
          <Link
            to="/vendor/login"
            data-testid={mobile ? "mobile-nav-vendor-login-btn" : "nav-vendor-login-btn"}
            onClick={() => setPortalOpen(false)}
            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50"
          >
            <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
              <Truck className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">Vendor Login</span>
              <span className="block text-[11px] text-slate-500 leading-snug">For network partners fulfilling services</span>
            </span>
          </Link>
        </div>
      )}
    </div>
  );

  // Right-side CTAs vary by auth state
  const renderRightCtas = (mobile = false) => {
    const cls = mobile ? "w-full" : "";
    if (user && user.role === "admin") {
      return (
        <>
          <Link
            to="/admin/dashboard"
            data-testid={mobile ? "mobile-nav-admin-dashboard-btn" : "nav-admin-dashboard-btn"}
            className={`${cls} px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap`}
          >
            Admin
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid={mobile ? "mobile-nav-logout-btn" : "nav-logout-btn"}
            className={`${cls} inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap hover:bg-slate-800 transition-all shadow-sm`}
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </>
      );
    }
    if (user && user.role === "clinic") {
      return (
        <>
          <Link
            to="/portal"
            data-testid={mobile ? "mobile-nav-portal-btn" : "nav-portal-btn"}
            className={`${cls} px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-colors whitespace-nowrap`}
          >
            Portal
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid={mobile ? "mobile-nav-logout-btn" : "nav-logout-btn"}
            className={`${cls} inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap hover:bg-slate-800 transition-all shadow-sm`}
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </>
      );
    }
    if (user && user.role === "vendor") {
      return (
        <>
          <Link
            to="/vendor"
            data-testid={mobile ? "mobile-nav-vendor-portal-btn" : "nav-vendor-portal-btn"}
            className={`${cls} px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-colors whitespace-nowrap`}
          >
            Vendor portal
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid={mobile ? "mobile-nav-logout-btn" : "nav-logout-btn"}
            className={`${cls} inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap hover:bg-slate-800 transition-all shadow-sm`}
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </>
      );
    }
    return (
      <>
        <PortalDropdown mobile={mobile} />
        <Link
          to="/request-access"
          data-testid={mobile ? "mobile-nav-request-access-btn" : "nav-request-access-btn"}
          className={`${cls} inline-flex items-center gap-1.5 bg-white text-slate-900 border border-slate-200 rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap hover:bg-slate-50 transition-all`}
        >
          Request access
        </Link>
        <Link
          to="/demo"
          data-testid={mobile ? "mobile-nav-book-demo-btn" : "nav-book-demo-btn"}
          className={`${cls} inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-1.5 text-xs font-semibold whitespace-nowrap hover:bg-slate-800 transition-all shadow-sm hover:shadow-md`}
        >
          Book a Demo
        </Link>
      </>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group shrink-0">
            <span className="inline-flex w-8 h-8 items-center justify-center text-[#1F4853] shrink-0">
              <NoderaMark size={28} />
            </span>
            <span className="font-display font-bold text-slate-900 text-lg tracking-tight whitespace-nowrap">
              Nodera <span className="text-slate-400 font-medium">Health</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-slate-900 bg-slate-100"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">{renderRightCtas(false)}</div>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 bg-white/80 text-slate-700"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-base font-medium ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">{renderRightCtas(true)}</div>
          </div>
        </div>
      )}
    </header>
  );
}
